import { FC, useEffect } from "react";
import { useTranslation } from 'react-i18next';

import { usePreferences } from "../../../utility/usePreferences";
import { DatePicker } from './common/date-picker';
import { DateTimePicker } from './common/date-time-picker';
import { mutateDate, getToday, getYesterday } from './common/date-utils';
import { ICriteria, IDateCriteriaConfig } from "./common/types";
import { Nullable } from "../../../types/utility";
import { useUserParams } from '../../../context/user-params-context';
import { useDate } from "../../../utility/useDate";

export const EndDateSelector: FC<ICriteria<IDateCriteriaConfig>> = ({ config }) => {
  const { userParams, setUserParams, clearParams } = useUserParams();
  const { timeZone } = usePreferences();
  const { localizeDate } = useDate();
  const { t } = useTranslation();

  const today = getToday(timeZone);
  const yesterday = getYesterday(timeZone);
  const tomorrow = mutateDate(today, { add: { days: 1 } });
  const oneWeekFromTomorrow = mutateDate(tomorrow, {
    add: { days: 6 }
  });

  // Determine if this is for a forecast tab (with unrestricted future dates)
  const isForecasting = config?.isForecast === true;

  const includeTime = config?.includeTime === true;

  // For forecasting: don't restrict max date, otherwise use config max or yesterday
  const maxDate = isForecasting
    ? undefined
    : (config?.maxDate ? mutateDate(today, config?.maxDate) : yesterday);

  // For forecasting: use tomorrow as minDate, otherwise use config min or undefined
  const minDate = isForecasting
    ? tomorrow
    : (config?.minDate ? mutateDate(today, config?.minDate) : undefined);

  useEffect(() => {
    let endDate;

    if (config?.defaultDate) {
      // Use config if provided
      endDate = mutateDate(today, config.defaultDate);

      // If not forecasting and the date is after yesterday, use yesterday instead
      if (!isForecasting && endDate > yesterday) {
        endDate = yesterday;
      }
    } else {
      // Default to yesterday for normal tabs, or a week from tomorrow for forecast tabs
      endDate = isForecasting
        ? oneWeekFromTomorrow
        : yesterday;
    }

    const localizedDate = localizeDate(endDate.toJSDate()).endOf('day');
    setUserParams({ "@u_endDate": localizedDate.toISO() });

    return () => {
      clearParams(['@u_endDate']);
    };
  }, [config]);

  const handleChangeDate = (value: Nullable<Date>) => {
    if (value) {
      const date = localizeDate(value).endOf('day').toISO();
      setUserParams({ "@u_endDate": date });
    }
  };

  const handleChangeDateTime = (value: Nullable<string>) => {
    if (value) {
      setUserParams({ "@u_endDate": value });
    }
  };

  if (includeTime) {
    return (
      <DateTimePicker
        minDate={minDate?.toJSDate()}
        maxDate={maxDate?.toJSDate()}
        value={userParams["@u_endDate"]}
        onChangeDateTime={handleChangeDateTime}
        label={t("booking.labels.endDate")}
        timeLabel={t("booking.labels.endDate") + " Time"}
        testIdPrefix="end-date"
      />
    );
  }

  return (
    <DatePicker
      minDate={minDate?.toJSDate()}
      maxDate={maxDate?.toJSDate()}
      value={userParams["@u_endDate"]}
      placeholderText={t("booking.labels.endDate")}
      onChangeDate={handleChangeDate}
      label={t("booking.labels.endDate")}
    />
  );
};

export default EndDateSelector;
